import { Button, Dropdown, Menu, Modal, Select } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { usePagination } from "hooks/usePagination";
import { useEffect, useState } from "react";
import { QUERY_KEY_FOR_JOBS, useGetJobs } from "../hooks/useGetJobs";
import { IJobsProps } from "../types";
import { useDelete } from "hooks/useDelete";
import { useClose } from "hooks/useClose";
import { routePath } from "config/routeMgt/routePath";
import { Link } from "react-router-dom";
import { useGlobalContext } from "stateManagers/GlobalContext";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useLikeJob } from "../hooks/useLikeJob";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { useGetTalentCount } from "features/home/hooks/useGetTalentCount";
import { useBookmarkJob } from "../hooks/useBookmark";
import CustomEmpty from "components/CustomEmpty";
import { useTour } from "components/layout/components/tours/TourGuideContext";
import dayjs from "dayjs";
import { TableRowShimmer } from "components/layout/components/loaders/Shimmer";
import { useCheckPermission } from "components/layout/sidebar/hooks/useHasPermission";
import { jobsCreationPath } from "utils/formHelpers/routeHelper";
import { formatDate } from "admin/Authentication/hooks/useDateFormat";
import { END_POINT } from "config/environment";
import {
  ensureMetaPixelForTrackedJob,
  trackMetaJobLeadForListing,
  trackMetaJobsListApplyClick,
  trackMetaJobsListViewDetailsClick,
} from "utils/metaPixelJob";

interface JobsTableProps {
  handleJob: (id: any) => void;
  searchTerm: string;
  activeTab: "all" | "liked" | "bookmarked" | "suggested";
  demoMode?: boolean;
  demoData?: IJobsProps[];
  jobDistributionPackageCount?: number | null;
}

const deleteEndPointUrl = "delete-job";
const closeEndPointUrl = "close-job";
export const JobsTable: React.FC<JobsTableProps> = ({
  handleJob,
  searchTerm,
  activeTab,
  demoMode = false,
  demoData = [],
  jobDistributionPackageCount,
}) => {
  const { filterJobState } = useGlobalContext();
  useDelete({
    queryKey: QUERY_KEY_FOR_JOBS,
    deleteEndPointUrl,
  });

  const { closeData } = useClose({
    queryKey: QUERY_KEY_FOR_JOBS,
    closeEndPointUrl,
  });

  const queryClient = useQueryClient();
  const { pagination, onChange } = usePagination();
  const { token, userInfo } = useGetUserInfo();
  const { mutate } = useLikeJob();
  const { mutate: bookmarkMutate } = useBookmarkJob();
  const { data: profileData } = useGetTalentCount();
  const [isCloseModalVisible, setIsCloseModalVisible] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState<string>("");
  const [closeReasonError, setCloseReasonError] = useState<string>("");
  const { running } = useTour();
  const { data, isLoading, refetch } = useGetJobs({
    pagination,
    search: searchTerm,
    title: filterJobState.title,
    jobposted: filterJobState.jobposted,
    closed: filterJobState.closed,
    employment_type: filterJobState.employment_type,
    sector: filterJobState.sector,
    experience_level: filterJobState.level,
    end_date: filterJobState.end_date,
    start_date: filterJobState.start_date,
    work_model: filterJobState.work_model,
    location_country: filterJobState.location_country,
    location_city: filterJobState.location_city,
    liked_by_user: !userInfo?.recruiter && activeTab === "liked" ? 1 : 0,
    bookmarked_by_user:
      !userInfo?.recruiter && activeTab === "bookmarked" ? 1 : 0,
  });
  const showDemoRow = running;

  const displayData = showDemoRow ? demoData : data?.data || [];

  const canEditJobs = useCheckPermission("job.edit");
  const canViewJobs = useCheckPermission("job.view");
  const canViewApplicants = useCheckPermission("job.viewapplicants");

  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    const text = html.replace(/<[^>]*>/g, "");
    return text.length > 300 ? text.substring(0, 300) + "..." : text;
  };

  const canUseJobDistributionShare = (): boolean => {
    if (
      jobDistributionPackageCount === undefined ||
      jobDistributionPackageCount === null
    ) {
      return true;
    }
    if (jobDistributionPackageCount > 0) {
      return true;
    }
    openNotification({
      title: "You do not have enough credits to share a job.",
      description: "Please upgrade your plan to share a job.",
      state: "error",
      duration: 4.5,
    });
    return false;
  };

  useEffect(() => {
    return () => {
      onChange({ current: 1, pageSize: pagination.pageSize });
    };
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (userInfo?.recruiter) {
        refetch();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [refetch, userInfo?.recruiter]);

  useEffect(() => {
    if (!userInfo?.recruiter) {
      onChange({ ...pagination, current: 1 });
    }
  }, [activeTab]);

  const getOgUrl = (slug: string) => `${END_POINT.BASE_URL}/og/jobs/${slug}`;

  const formatShareText = (
    title: string,
    jobsummary: string,
    location: string,
    expiryDate: string,
  ) => {
    const cleanDescription = stripHtmlTags(
      jobsummary || "No description available",
    );
    return `✨ JOB OPPORTUNITY ✨\n\n📌 Position: ${title}\n📍 Location: ${location}\n📅 Expires: ${expiryDate}\n\n📝 About the role:\n${cleanDescription}\n\n`;
  };

  const handleShareFacebook = (
    slug: string,
    title: string,
    jobsummary: string,
    location: string,
    expiryDate: string,
  ) => {
    if (!canUseJobDistributionShare()) return;

    const url = `${window.location.origin}${routePath.jobDetails(slug).path}`;
    const shareText = formatShareText(title, jobsummary, location, expiryDate);
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(shareText)}`;
    window.open(fbUrl, "_blank", "width=600,height=500");
  };

  const handleShareTwitter = (
    slug: string,
    title: string,
    jobsummary: string,
    location: string,
    expiryDate: string,
  ) => {
    if (!canUseJobDistributionShare()) return;

    const url = `${window.location.origin}${routePath.jobDetails(slug).path}`;
    const shareText = formatShareText(title, jobsummary, location, expiryDate);
    const maxLength = 240;
    const truncatedText =
      shareText.length > maxLength
        ? shareText.substring(0, maxLength - 20) + "..."
        : shareText;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(truncatedText)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, "_blank", "width=600,height=500");
  };

  const handleShareLinkedin = (
    slug: string,
    title: string,
    jobsummary: string,
    location: string,
  ) => {
    if (!canUseJobDistributionShare()) return;

    const url = `${window.location.origin}${routePath.jobDetails(slug).path}`;
    const cleanDescription = stripHtmlTags(
      jobsummary || "No description available",
    );

    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

    window.open(linkedinUrl, "_blank", "width=600,height=500");
  };

  const handleShareInstagram = (
    slug: string,
    title: string,
    jobsummary: string,
    location: string,
    expiryDate: string,
  ) => {
    if (!canUseJobDistributionShare()) return;

    const url = `${window.location.origin}${routePath.jobDetails(slug).path}`;
    const shareText = `${formatShareText(title, jobsummary, location, expiryDate)}\n🔗 Apply here: ${url}`;

    navigator.clipboard
      .writeText(shareText)
      .then(() => {
        openNotification({
          title: "Copied to Clipboard",
          description:
            "Job details copied! Paste them into your Instagram post or story.",
          state: "success",
        });
      })
      .catch(() => {
        openNotification({
          title: "Copy Failed",
          description: "Failed to copy job details to clipboard",
          state: "error",
        });
      });
  };

  const handleCopyLink = (slug: string) => {
    if (!canUseJobDistributionShare()) return;

    const url = `${window.location.origin}${routePath.jobDetails(slug).path}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        openNotification({
          title: "Copied!",
          description: "Job link copied to clipboard",
          state: "success",
        });
      })
      .catch(() => {
        openNotification({
          title: "Copy Failed",
          description: "Failed to copy link to clipboard",
          state: "error",
        });
      });
  };

  // const isProfileComplete = profileData?.profile.total >= 75;
  const isProfileComplete = profileData?.profile.total >= 0;
  const likeJob = (
    id: any,
    hasLiked: boolean,
    meta?: { slug?: string; title?: string },
  ) => {
    if (!isProfileComplete) {
      openNotification({
        title: "Incomplete Profile",
        description: "You need at least 75% profile completion to like jobs.",
        state: "error",
        duration: 4.5,
      });
      return;
    }
    if (!hasLiked) {
      trackMetaJobsListApplyClick({
        id,
        slug: meta?.slug,
        title: meta?.title,
      });
    }
    const props: any = {
      token,
      id: id,
    };

    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: hasLiked
            ? "Job unliked successfully."
            : "Job liked successfully.",
          duration: 4.5,
        });
        if (!hasLiked && !userInfo?.recruiter) {
          trackMetaJobLeadForListing({
            id,
            slug: meta?.slug,
            title: meta?.title,
          });
        }
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_JOBS],
        });
      },
    });
  };

  const handleBookmark = (id: any) => {
    if (!isProfileComplete) {
      openNotification({
        title: "Incomplete Profile",
        description:
          "You need at least 75% profile completion to bookmark jobs.",
        state: "error",
        duration: 4.5,
      });
      return;
    }

    const props: any = {
      token,
      id: id,
    };

    bookmarkMutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Job bookmark updated successfully.",
          duration: 4.5,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_FOR_JOBS],
        });
      },
    });
  };

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  let corporate_id = null;
  if (userInfo?.recruiter && userInfo?.recruiter.is_corporate === 1) {
    corporate_id = userInfo?.current_team
      ? userInfo?.current_team.corporate_id
      : userInfo?.recruiter.is_corporate === 1
        ? userInfo?.recruiter.id
        : "";
  }

  const formatExperienceLevel = (level: string): string => {
    return level
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  let talent_id = null;
  if (!userInfo?.recruiter) {
    talent_id = userInfo?.talent;
  }

  const closeReasons = [
    "Position Filled",
    "Hiring Cancelled",
    "Duplicate Posting",
    "Updated Role",
    "Incorrect Information",
    "Pause Hiring",
  ];

  const handleConfirmCloseJob = () => {
    if (!closeReason) {
      setCloseReasonError("Please select a reason");
      return;
    }

    if (selectedJobId) {
      closeData(selectedJobId, closeReason);
      setIsCloseModalVisible(false);
      setCloseReason("");
      setSelectedJobId(null);
    }
  };

  const openCloseJobModal = (jobId: string) => {
    setSelectedJobId(jobId);
    setIsCloseModalVisible(true);
    setCloseReason("");
    setCloseReasonError("");
  };

  useEffect(() => {
    if (filterJobState) {
      onChange({ current: 1, pageSize: pagination.pageSize });
    }
  }, [JSON.stringify(filterJobState)]);

  useEffect(() => {
    void ensureMetaPixelForTrackedJob();
  }, []);

  const columns: ColumnsType<IJobsProps> = [
    {
      title: "Name",
      dataIndex: "title",
      render: (_, val) => (
        <div>
          <Link
            to={routePath.jobDetails(val.slug).path}
            className="font-medium text-primary block"
            onClick={() =>
              trackMetaJobsListViewDetailsClick({
                id: val.id,
                slug: val.slug,
                title: val.title,
              })
            }
          >
            {val.title}
          </Link>
        </div>
      ),
    },
    ...(corporate_id != null
      ? [
          {
            title: "Posted by",
            dataIndex: "posted_by",
            render: (_: any, val: any) => <span>{val.userInfo?.name}</span>,
          },
        ]
      : []),

    {
      title: "Organization",
      dataIndex: "company_name",
    },
    ...(talent_id != null
      ? [
          {
            title: "Recruiter",
            dataIndex: "recruiter",
            render: (_: any, val: any) => (
              <div>
                <Link
                  to={routePath.recruiterDetails(val.recruiter_id).path}
                  className="font-medium text-primary block"
                >
                  {val.recruiter.first_name} {val.recruiter.last_name}{" "}
                  {val.recruiter.middle_name}
                </Link>
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Location",
      dataIndex: "location_city",
      render: (_, val) => (
        <span className="capitalize">
          {val.location_city}, {val.location_country}
        </span>
      ),
    },
    {
      title: "Date Posted",
      dataIndex: "created_at",
      render: (_, val) => formatDate(val.created_at),
    },
    {
      title: "Job Post Expiry Date",
      dataIndex: "expiry_date",
      render: (_, val) => formatDate(val.expiry_date),
    },

    {
      title: "Experience Level",
      dataIndex: "level",
      render: (_, val) => (
        <span className="capitalize">{formatExperienceLevel(val.level)}</span>
      ),
    },
    {
      title: "Job Status",
      dataIndex: "status",
      render: (_, val) => (
        <span
          className={`capitalize font-medium px-2 py-0.5 rounded-md ${
            val?.closed === 1
              ? "bg-red-100 text-red-600"
              : val?.status === "draft"
                ? "bg-gray-100 text-gray-600"
                : val?.expiry_date &&
                    dayjs().isAfter(dayjs(val.expiry_date), "day")
                  ? "bg-red-100 text-red-600"
                  : "bg-green-100 text-green-600"
          }`}
        >
          {val?.status === "draft"
            ? "Draft"
            : val?.expiry_date && dayjs().isAfter(dayjs(val.expiry_date), "day")
              ? "Expired"
              : val?.closed === 1
                ? "Closed"
                : "Open"}
        </span>
      ),
    },

    ...(talent_id != null
      ? [
          {
            title: "Date Closed",
            dataIndex: "closed_at",
            render: (text: any, record: any) => {
              if (record.closed === 1 && text) {
                const date = new Date(text);
                return (
                  <span className="capitalize text-red-600">
                    {formatDate(date)}
                  </span>
                );
              } else if (new Date(record.expiry_date) < new Date()) {
                return (
                  <span className="capitalize text-red-600">Job Expired</span>
                );
              }

              return <span className="capitalize">Still Open</span>;
            },
          },
        ]
      : []),

    {
      title: "Action",
      dataIndex: "action",
      render: (_, val) => (
        <div className="flex items-center gap-2">
          {userInfo?.recruiter ? (
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu>
                  {canEditJobs &&
                    ((val?.expiry_date &&
                      dayjs().isBefore(dayjs(val.expiry_date), "day")) ||
                      val.status == "draft") && (
                      <Menu.Item key="edit" data-tour="edit-applicants">
                        <Link
                          to={
                            val.status === "draft"
                              ? jobsCreationPath(val.slug)
                              : jobsCreationPath(val.slug + "___edit")
                          }
                        >
                          Edit
                        </Link>
                      </Menu.Item>
                    )}

                  {canViewJobs && (
                    <Menu.Item key="view" data-tour="view-job">
                      <Link to={routePath.jobDetails(val.slug).path}>View</Link>
                    </Menu.Item>
                  )}

                  {canEditJobs && val.closed === 1 && (
                    <Menu.Item key="reopen-job" data-tour="reopen-job">
                      <Link to={jobsCreationPath(`${val.slug}___reopen`)}>
                        Reopen
                      </Link>
                    </Menu.Item>
                  )}

                  {val.closed === 0 && canViewJobs && (
                    <Menu.Item
                      key="close-job"
                      data-tour="close-job"
                      onClick={() => openCloseJobModal(val.id)}
                    >
                      Close Job
                    </Menu.Item>
                  )}
                  {canViewApplicants && (
                    <Menu.Item key="4" data-tour="view-applicants">
                      <Link to={routePath.viewApplicants(val.id).path}>
                        View Applicants
                      </Link>
                    </Menu.Item>
                  )}

                  <Menu.SubMenu
                    key="6"
                    title="Share"
                    data-tour="share-job"
                    disabled={val.closed === 1}
                  >
                    <Menu.Item
                      key="6-1"
                      onClick={() =>
                        handleShareFacebook(
                          val.slug!,
                          val.title,
                          val.jobsummary ?? "No Job Description",
                          val.location_city,
                          val.expiry_date,
                        )
                      }
                    >
                      Facebook
                    </Menu.Item>
                    <Menu.Item
                      key="6-2"
                      onClick={() =>
                        handleShareTwitter(
                          val.slug!,
                          val.title,
                          val.jobsummary ?? "No Job Description",
                          val.location_city,
                          val.expiry_date,
                        )
                      }
                    >
                      X
                    </Menu.Item>
                    <Menu.Item
                      key="6-3"
                      onClick={() =>
                        handleShareLinkedin(
                          val.slug!,
                          val.title,
                          val.jobsummary ?? "No Job Description",
                          val.location_city,
                        )
                      }
                    >
                      LinkedIn
                    </Menu.Item>
                    <Menu.Item
                      key="6-4"
                      onClick={() =>
                        handleShareInstagram(
                          val.slug!,
                          val.title,
                          val.jobsummary ?? "No Job Description",
                          val.location_city,
                          val.expiry_date,
                        )
                      }
                    >
                      Instagram
                    </Menu.Item>
                    <Menu.Item
                      key="6-5"
                      onClick={() => handleCopyLink(val.slug!)}
                    >
                      Copy Link
                    </Menu.Item>
                  </Menu.SubMenu>
                </Menu>
              }
            >
              <i
                className="ri-more-2-fill text-lg cursor-pointer"
                data-tour="job-actions"
                id="job-actions"
              ></i>
            </Dropdown>
          ) : (
            <>
              <i
                onClick={() => {
                  if (!val.has_liked) {
                    likeJob(val.id, false, {
                      slug: val.slug,
                      title: val.title,
                    });
                  }
                }}
                className={`${val.has_liked ? "ri-heart-fill text-red-500" : "ri-heart-line"} text-lg cursor-pointer`}
                style={{
                  pointerEvents: val.has_liked ? "none" : "auto",
                  opacity: val.has_liked ? 0.6 : 1,
                }}
                title={val.has_liked ? "Already liked" : "Like job"}
              ></i>

              <i
                onClick={() => {
                  return handleBookmark(val.id);
                }}
                className={`${
                  val.has_bookmarked
                    ? "ri-bookmark-fill text-primary"
                    : "ri-bookmark-line"
                } text-lg cursor-pointer`}
                data-joyride="bookmark-job"
                style={{
                  pointerEvents: isProfileComplete ? "auto" : "none",
                  opacity: isProfileComplete ? 1 : 0.5,
                }}
              ></i>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? (
        <TableRowShimmer />
      ) : displayData.length === 0 ? (
        <div className="mt-8">
          <CustomEmpty message="No jobs added yet" />
        </div>
      ) : !userInfo?.recruiter && displayData?.length > 0 ? (
        <Table
          className="bg-white rounded-md shadow border mt-8"
          columns={columns}
          dataSource={displayData.map((item) => ({
            ...item,
            key: item.id,
          }))}
          loading={isLoading}
          scroll={{ x: 550 }}
          pagination={{ ...pagination, total: data?.total }}
          onChange={onChange}
          rowClassName={(record) =>
            running && record.id === 999 ? "demo-job-row" : ""
          }
        />
      ) : (
        <Table
          className="bg-white rounded-md shadow border mt-8"
          columns={columns}
          dataSource={displayData.map((item) => ({
            ...item,
            key: item.id,
          }))}
          rowSelection={rowSelection}
          loading={isLoading}
          scroll={{ x: 550 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} Jobs`,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={onChange}
        />
      )}

      <Modal
        title="Close Job"
        open={isCloseModalVisible}
        onCancel={() => setIsCloseModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsCloseModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger
            onClick={handleConfirmCloseJob}
          >
            Confirm Close
          </Button>,
        ]}
      >
        <div className="mb-4">
          <p className="mb-2">Please select a reason for closing this job:</p>
          <Select
            placeholder="Select a reason"
            style={{ width: "100%" }}
            value={closeReason}
            onChange={(value) => {
              setCloseReason(value);
              if (value) setCloseReasonError("");
            }}
            options={closeReasons.map((reason) => ({
              label: reason,
              value: reason,
            }))}
          />
          {closeReasonError && (
            <p className="text-red-500 text-sm mt-1">{closeReasonError}</p>
          )}
        </div>
      </Modal>
    </div>
  );
};
